import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Divider,
  Modal,
  Popconfirm,
  Select,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import { ImportOutlined, MinusOutlined, PlusOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  clearStreamReturn,
  openMessage,
  socketEmitEvent,
} from '../../../redux/actions';
import * as SERVICE from '../../../constants/service.types';
import { cloneDeep } from 'lodash';
import 'react-leaflet-markercluster/dist/styles.min.css';
import { Map, Marker, TileLayer } from 'react-leaflet';
import { getAddress } from '../../../api/locationApi';
import AppInputSelect from '../../../components/Form/InputSelect';
import storageService from '../../../services/storage.service';
import { NOTIFIES } from '../../../constants/system.keys';
import { updateRecoveryCameraPage } from '../../../redux/recovery/action';

const ImportListCamera = ({ setAddMethod, gatewaySerial, onDone }) => {
  const { checkStreamSources, namespace } = useSelector((state) => state);
  const token = storageService.getInternalStorage()?.decodedAccessToken;

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [loaded, setLoaded] = useState(false);
  const [modalImportedFileVisible, setModalImportedFileVisible] =
    useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [processing, setProcessing] = useState(false);

  const convertedFile = useRef(null);
  const address = useRef(null);

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      ellipsis: true,
      render: (text) => <Tooltip title={text}>{text}</Tooltip>,
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      width: 150,
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <div>{text}</div>
        </Tooltip>
      ),
    },
    {
      title: 'Sub-Source',
      dataIndex: 'subSources',
      key: 'subSources',
      width: 150,
      ellipsis: true,
      render: (_text, record) => {
        if (!record.subSources) return null;
        const subSources = record.subSources.split(';');
        return subSources.map((subSource, index) => (
          <Tooltip key={index} title={subSource}>
            <div className="text-ellipsis">{subSource}</div>
          </Tooltip>
        ));
      },
    },
    { title: 'Codec', dataIndex: 'codec', key: 'codec', width: 100 },
    {
      title: 'Resolution',
      dataIndex: 'resolution',
      key: 'resolution',
      width: 120,
      render: (_text, record) => {
        if (record.resolutionWidth && record.resolutionHeight) {
          return `${record.resolutionWidth} x ${record.resolutionHeight}`;
        }
        return null;
      },
    },
    // { title: 'FPS', dataIndex: 'fps', key: 'fps', width: 100, },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
      width: 300,
      ellipsis: true,
      render: (_text, record) => (
        <AppInputSelect
          value={record?.addressValue}
          onChange={(value) => {
            updateDataSource(record.id, value);
          }}
          options={record.address?.map((option) => ({
            label: option,
            value: option,
          }))}
        />
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (_text, record) => {
        return (
          <Tag
            color={record.authenticationRtsp === 'require' ? 'yellow' : 'green'}
          >
            {record.authenticationRtsp?.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: 'Action',
      dataIndex: '',
      width: 100,
      key: '',
      render: (_text, record) => {
        const unAddAble = record.authenticationRtsp === 'require';
        return (
          <div className="btns-import-table">
            <Popconfirm
              title={t('Are you sure？')}
              onConfirm={() => {
                handleDeleteCamera(record.id);
              }}
              okText={t('Yes')}
              cancelText={t('No')}
            >
              <Button type="primary" danger size="small">
                <MinusOutlined />
              </Button>
            </Popconfirm>
            <Popconfirm
              title={t('Are you sure？')}
              onConfirm={() => {
                handleAddCamera(record);
              }}
              okText={t('Yes')}
              cancelText={t('No')}
              disabled={unAddAble}
            >
              <Button type="primary" disabled={unAddAble} size="small">
                <PlusOutlined />
              </Button>
            </Popconfirm>
          </div>
        );
      },
    },
  ];

  const getContact = (camera) => {
    if (camera?.serial && namespace) {
      let dataBody = {
        namespace,
        serial: camera?.serial,
      };
      let dataObj = {
        appServiceType: SERVICE.GET_DEVICE_CONTACT_INFO,
        requestId: new Date().getTime(),
        body: JSON.stringify(dataBody),
      };
      dispatch(socketEmitEvent(dataObj));
    }
  };

  const wait = (milliseconds = 2000) => {
    return new Promise((resolve) =>
      setTimeout(() => {
        resolve(true);
      }, milliseconds),
    );
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) {
      return;
    }
    const reader = new FileReader();

    reader.onload = async (event) => {
      setLoaded(true);
      const data = new Uint8Array(event.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];

      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      convertedFile.current = jsonData
        .slice(1)
        .map(
          (
            [name, source, subSources, lat, lng, tags, gatewaySerial],
            index,
          ) => ({
            name,
            source,
            subSources,
            lat,
            lng,
            tags,
            gatewaySerial,
            id: index,
          }),
        )
        ?.filter((item) => item.source);

      const latLngs = convertedFile.current?.filter(
        (item) => item.lat && item.lng,
      );
      const promises = latLngs.map(
        async (item) =>
          await getAddress(
            token,
            { namespace },
            { lat: item.lat, lng: item.lng },
          ),
      );
      const response = await Promise.allSettled(promises);
      address.current = response
        ?.map((item) => item.value?.data?.record)
        ?.filter((item) => !!item);
    };

    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const updateDataSource = (id, value) => {
    setDataSource(
      dataSource.map((item) => {
        if (Number(item.id) === Number(id)) {
          return {
            ...item,
            addressValue: value,
          };
        }
        return item;
      }),
    );
  };

  const findAddress = (lat, lng) => {
    return address.current?.find((item) => item.lat === lat && item.lng === lng)
      ?.address;
  };

  const handleScan = () => {
    console.log('🚀 ~ handleScan ~ convertedFile:', convertedFile);
    for (let i = 0; i < convertedFile.current?.length; i++) {
      const data = {
        source: convertedFile.current[i].source,
        namespace,
        gatewaySerial:
          convertedFile.current[i]?.gatewaySerial || gatewaySerial || '',
        gatewayType: 'premise_box',
      };
      console.log('🚀 ~ handleScan ~ data:', data);
      let dataObj = {
        appServiceType: SERVICE.REQUEST_CHECK_STREAM,
        requestId: convertedFile.current[i].id,
        body: JSON.stringify(data),
      };

      dispatch(socketEmitEvent(dataObj));
    }
  };

  const handleAddExternalDevice = (camera) => {
    const subSources = convertedFile.current?.find(
      (item) => item.id === Number(camera?.requestId),
    )?.subSources;
    const data = {
      serial: camera?.serial,
      namespace,
      extraInfo: {
        ...(camera || {}),
      },
    };
    if (subSources) {
      data.extraInfo.subSources = JSON.stringify(
        subSources.split(';').map((item) => ({ source: item })),
      );
    }

    console.log('🚀 ~ handleAddExternalDevice ~ data:', data);
    const dataObj = {
      appServiceType: SERVICE.REQUEST_ADD_EXTERNAL_DEVICE,
      requestId: camera?.requestId,
      body: JSON.stringify(data),
    };

    dispatch(socketEmitEvent(dataObj));
  };

  const handleUpdateDevice = (camera) => {
    const information = convertedFile.current?.find(
      (item) => item.id === Number(camera?.requestId),
    );
    const { lat, lng, tags, name } = information;

    const data = {
      note: camera?.addressValue,
      lat: lat || 0,
      lng: lng ?? 0,
      serial: camera?.serial,
      name: name ? name : camera?.serial,
      namespace,
      tags: tags?.toLowerCase(),
    };

    const dataObj = {
      appServiceType: SERVICE.UPDATE_DEVICE,
      body: JSON.stringify(data),
    };

    dispatch(socketEmitEvent(dataObj));
  };

  const handleDeleteCamera = (id) => {
    const filteredSourceData = dataSource.filter((item) => item.id !== id);
    setDataSource(filteredSourceData);
  };

  const onNext = () => {
    setProcessing(true);
    handleScan();
  };

  const handleAddCamera = async (camera) => {
    handleAddExternalDevice(camera);
    await wait(1000);
    handleUpdateDevice(camera);
    await wait(1000);
    onDone(true);
    handleDeleteCamera(camera.id);
  };

  useEffect(() => {
    console.log(
      '🚀 ~ ImportListCamera ~ checkStreamSources1:',
      checkStreamSources,
    );
    console.log(
      '🚀 ~ ImportListCamera ~ convertedFile?.current:',
      convertedFile?.current,
    );
    const checkStreamSourcesReturn = checkStreamSources?.filter((item) =>
      convertedFile?.current?.some(
        (cf) => cf?.id === item?.requestId && item?.source === cf?.data?.source,
      ),
    );
    console.log(
      '🚀 ~ ImportListCamera ~ checkStreamSourcesReturn:',
      checkStreamSourcesReturn,
    );
    if (
      convertedFile?.current &&
      checkStreamSourcesReturn?.length === convertedFile?.current?.length
    ) {
      const streamSources = cloneDeep(
        checkStreamSourcesReturn?.filter((item) => item?.result > 0),
      );
      if (streamSources?.length > 0) {
        streamSources.forEach((streamSource) => {
          const convertedData = convertedFile.current?.find(
            (item) => item.id === streamSource.requestId,
          );
          if (convertedData) {
            streamSource.convertedData = convertedData;

            if (convertedData.lat && convertedData.lng) {
              const address = findAddress(convertedData.lat, convertedData.lng);
              streamSource.convertedData.address = address;
              streamSource.convertedData.addressValue = address?.[0];
            }
          }
        });
        console.log(
          '🚀 ~ ImportListCamera ~ checkStreamSources2:',
          checkStreamSourcesReturn,
        );
        setDataSource(
          streamSources.map((streamSource) => ({
            ...streamSource?.data,
            ...streamSource.convertedData,
          })),
        );
        setProcessing(false);
        setModalImportedFileVisible(true);
        dispatch(clearStreamReturn([]));
      } else {
        openMessage(NOTIFIES.ERROR, t('Check stream failed'));
        setProcessing(false);
        setLoaded(false);
      }
    }
  }, [checkStreamSources, dispatch, t]);

  return (
    <div className="import-list-camera">
      <Modal
        title={t('Import list camera')}
        className=""
        visible={modalImportedFileVisible}
        onOk={() => {}}
        onCancel={() => setModalImportedFileVisible(false)}
        width="85%"
        footer={false}
        maskClosable={false}
      >
        <Table
          rowKey={(record) => record.id}
          columns={columns}
          onExpand={(expanded, record) => {
            getContact(record);
          }}
          expandable={{
            expandedRowRender: (record) => {
              const position = [record.lat, record.lng];
              return (
                <div className="expandable-row">
                  <img src={record.thumbnail} alt={record.name} />
                  <div>
                    <Map
                      style={{ width: '400px', height: '400px' }}
                      center={position}
                      zoom={13}
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      <Marker position={position} />
                    </Map>
                    <div></div>
                  </div>
                </div>
              );
            },
            rowExpandable: (record) => record?.lat && record?.lng,
          }}
          dataSource={dataSource}
          isLoading={true}
          className="imported-camera-list-table"
        />
      </Modal>

      <div
        style={{
          fontSize: 12,
          color: '#ffffffa6',
          textAlign: 'center',
          margin: '10px 0',
        }}
      >
        <a
          style={{ textDecoration: 'underline' }}
          href="https://db1.beex.vn/camera_import.xlsx"
        >
          {t('Download template')}
        </a>
        &nbsp;
        {t(
          'and fill in camera information. Then import the file to add cameras',
        )}
      </div>

      <input
        type="file"
        accept=".xlsx"
        onChange={(e) => {
          handleFileUpload(e);
        }}
        style={{ display: 'none' }}
        id="fileUpload"
      />
      <div className="btn-import">
        <Button
          icon={<ImportOutlined />}
          type="primary"
          onClick={() => {
            document.getElementById('fileUpload').click();
          }}
        >
          {t('Import list camera')}
        </Button>
      </div>

      <Divider />

      <div className="btn-next">
        <Button
          type="secondary"
          onClick={() => {
            setAddMethod(null);
            dispatch(updateRecoveryCameraPage({ rtspAddMethod: null }));
          }}
        >
          {t('Back')}
        </Button>
        <Button
          type="primary"
          disabled={!loaded || processing}
          loading={processing}
          onClick={() => {
            onNext();
          }}
        >
          {processing ? t('Processing') : t('Next')}
        </Button>
      </div>
    </div>
  );
};

export default ImportListCamera;
